/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, Button, Paper, Divider } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/ko';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import * as eqpMonitorApi from '../../lib/api/eqpMonitor';
import MobileFilterCollapse from '../common/form/MobileFilterCollapse';
import EqpDayTimeline from './EqpDayTimeline';
import EqpHourlyStateChart from './EqpHourlyStateChart';
import EqpSpindleFeedDetail from './EqpSpindleFeedDetail';
import EqpEventListPanel from './EqpEventListPanel';
import { computeZoomHourRange } from './eqpHourly';

const AUTO_REFRESH_MS = 30000; // 오늘 날짜를 보고 있을 때만 돈다(지난 날짜는 더 이상 안 바뀐다)
const PAGE_SIZE = 20;

/**
 * 하루치 수집 데이터(일중 타임라인 + 시간대별 차트 + 이벤트 목록) - 장비 하나를 고정해 보여준다.
 * 독립 메뉴(수집 데이터, EqpDataMngContainer - 자체 장비 선택기를 가진다)와 장비 상세창의
 * "수집 데이터" 탭이 이 컴포넌트를 함께 쓴다. 장비를 고르는 UI는 이 컴포넌트 밖(호출자)에서
 * filterPrefix 로 끼워 넣는다 - 상세창 탭은 이미 장비가 고정돼 있어 필요 없다.
 */
const EqpDayDataView = ({ eqpUuid, machineLabel, filterPrefix }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [date, setDate]         = useState(dayjs());
  const [timeline, setTimeline] = useState(null);
  const [metrics, setMetrics]   = useState([]);
  const [events, setEvents]     = useState([]);
  const [eventTotal, setEventTotal] = useState(0);
  const [eventPage, setEventPage]   = useState(0);
  const [typFilter, setTypFilter]   = useState('');
  const [loading, setLoading]       = useState(false);

  const dateStr = date.format('YYYY-MM-DD');
  const isToday = dateStr === dayjs().format('YYYY-MM-DD');

  const fetchMain = useCallback(async (showSpinner) => {
    if (!eqpUuid) return;
    if (showSpinner) setLoading(true);
    try {
      const [timelineRes, metricsRes] = await Promise.all([
        eqpMonitorApi.getMonitorTimeline({ eqpUuid, date: dateStr }),
        eqpMonitorApi.getMonitorMetrics({ eqpUuid, date: dateStr }),
      ]);
      setTimeline(eqpMonitorApi.extractPayload(timelineRes));
      setMetrics(eqpMonitorApi.extractList(metricsRes));
    } catch (e) {
      enqueueSnackbar('수집 데이터 조회 실패', { variant: 'error' });
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [eqpUuid, dateStr]);

  const fetchEvents = useCallback(async () => {
    if (!eqpUuid) return;
    try {
      const res = await eqpMonitorApi.getMonitorEvents({
        eqpUuid,
        from: `${dateStr} 00:00:00`,
        to: `${dateStr} 23:59:59`,
        typ: typFilter || undefined,
        page: eventPage + 1, // 표는 0부터, API 는 다른 서버 목록들과 맞춰 1부터
        size: PAGE_SIZE,
      });
      setEvents(eqpMonitorApi.extractList(res));
      setEventTotal(eqpMonitorApi.extractTotal(res));
    } catch (e) {
      enqueueSnackbar('이벤트 조회 실패', { variant: 'error' });
    }
  }, [eqpUuid, dateStr, typFilter, eventPage]);

  useEffect(() => { fetchMain(true); }, [fetchMain]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // 오늘 날짜만 자동 갱신
  useEffect(() => {
    if (!isToday) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') { fetchMain(false); fetchEvents(); }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [isToday, fetchMain, fetchEvents]);

  // 장비·날짜·종류필터가 바뀌면 이벤트 페이지는 처음부터 다시
  useEffect(() => { setEventPage(0); }, [eqpUuid, dateStr, typFilter]);

  const summaryText = useMemo(
    () => (machineLabel ? `${machineLabel} · ${dateStr}` : dateStr),
    [machineLabel, dateStr],
  );

  // 시간대별 차트(가동 현황·가공수)와 일중 타임라인이 공유하는 확대 범위 - 셋이 따로
  // 계산하면 범위가 어긋날 수 있어 여기 한 곳에서만 정해 내려준다.
  const zoomRange = useMemo(
    () => computeZoomHourRange({ isToday, intervals: timeline?.intervals || [], metrics, now: dayjs() }),
    [isToday, timeline, metrics],
  );

  return (
    <Box>
      <MobileFilterCollapse summary={summaryText}>
        <Grid container spacing={1.5} alignItems="center">
          {filterPrefix}
          <Grid item xs={8} sm={4} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DatePicker
                label="날짜" format="YYYY-MM-DD"
                value={date}
                maxDate={dayjs()}
                onChange={(v) => v && setDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={4} sm={3} md={2}>
            <Button
              variant="outlined" startIcon={<RefreshIcon />}
              onClick={() => { fetchMain(true); fetchEvents(); }}
            >
              새로고침
            </Button>
          </Grid>
        </Grid>
      </MobileFilterCollapse>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>일중 타임라인</Typography>
        <EqpDayTimeline
          date={date}
          intervals={timeline?.intervals || []}
          summary={timeline?.summary}
          loading={loading}
          isToday={isToday}
          zoomRange={zoomRange}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>시간대별 가동 현황</Typography>
        <EqpHourlyStateChart
          intervals={timeline?.intervals || []}
          date={date}
          isToday={isToday}
          zoomRange={zoomRange}
          loading={loading}
        />
      </Paper>

      <Box sx={{ mt: 2 }}>
        <EqpSpindleFeedDetail
          metrics={metrics}
          intervals={timeline?.intervals || []}
          date={date}
          zoomRange={zoomRange}
          loading={loading}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>이벤트 목록</Typography>
        <Divider sx={{ mb: 1.5 }} />
        <EqpEventListPanel
          list={events}
          total={eventTotal}
          loading={loading}
          typFilter={typFilter}
          onTypFilterChange={setTypFilter}
          page={eventPage}
          pageSize={PAGE_SIZE}
          onPageChange={(model) => setEventPage(model.page)}
        />
      </Paper>
    </Box>
  );
};

export default EqpDayDataView;
